import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import Select from "react-select";
import DataTable from "react-data-table-component";
import toast, { LoaderIcon } from "react-hot-toast";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import {
    getPriceListByOrganizationId,
    getOrgKeyValue,
    getSyncedPriceListByOrganizationId,
    proceedToSyncPrices,
} from "../../../services";

const defaultSelectValue = { label: "", value: "" };

const PriceSyncModal = ({ showSyncModal, toggleSyncModal }) => {
    const [isPending, setIsPending] = useState(false);

    const [orgOptionList, setOrgOptionList] = useState([]);
    const [syncOrg, setSyncOrg] = useState({
        from: defaultSelectValue,
        to: defaultSelectValue,
    });
    const [orgPriceList, setOrgPriceList] = useState([]);
    const [priceListToSync, setPriceListToSync] = useState([]);
    const [orgSyncedPriceList, setOrgSyncedPriceList] = useState([]);
    const [dataTableData, setDataTableData] = useState([])
    const [searchTerm, setSearchTerm] = useState("");
    const rowRefs = useRef([]);

    useEffect(() => {
        getOrgKeyValue().then((res) => {
            if (res.errorCode === 0) {
                const data = res.responsePacket;
                const options = data.map((d) => ({
                    value: d.key,
                    label: d.value,
                }));
                setSyncOrg((prev) => ({
                    ...prev,
                    from: options[0]
                }))
                setOrgOptionList(options);
                if (options[0].value !== null || options[0].value !== "") {
                    const rObj = { page: 0, size: -1, search: "", uuid: options[0].value };
                    getPriceListByOrganizationId(rObj).then((res) => {
                        if (res.errorCode === 0) {
                            var d = res.responsePacket.data;
                            setOrgPriceList(d);
                            setDataTableData(d);
                        } else {
                            toast(res.message);
                        }
                    });
                }
            } else {
                toast(res.message);
            }
        });
    }, []);

    const priceListColumns = [
        {
            name: "Name",
            cell: (row, index) => {
                const highlightedTitle = searchTerm ? 
                    row.title.split(new RegExp(`(${searchTerm})`, 'gi')).map((part, i) => {
                        return i % 2 !== 0 ? <span style={{ background: 'orange' }} key={i}>{part}</span> : part;
                    }) : row.title;
    
                return (
                    <div
                        ref={(el) => (rowRefs.current[index] = el)}
                    >
                        {highlightedTitle}
                    </div>
                );
            },
        },
    ];


    const priceListColumns2 = [
        {
            name: "Name",
            cell: (row) => row.title,
        },
    ];

    const handleOrgChange = (e, key) => {
        setSyncOrg((prev) => ({ ...prev, [key]: e }));

        if ((e.value !== null || e.value !== "") && key === "from") {
            const rObj = { page: 0, size: -1, search: "", uuid: e.value };
            getPriceListByOrganizationId(rObj).then((res) => {
                if (res.errorCode === 0) {
                    var d = res.responsePacket.data;
                    setOrgPriceList(d);
                    setDataTableData(d);
                } else {
                    toast(res.message);
                }
            });
        }
        if ((e.value !== null || e.value !== "") && key === "to") {
            getSyncedPriceList(e.value);
            
        }
    };

    const getSyncedPriceList = async (orgId) => {
        try {
            const rObj = {
                page: 0,
                search: "",
                size: -1,
                uuid: orgId,
            };
            const { errorCode, responsePacket } = await getSyncedPriceListByOrganizationId(rObj);
            if (errorCode === 0) {
                const syncedData = responsePacket.data;
                setOrgSyncedPriceList(syncedData);
                if (syncedData.length > 0) {
                    const previouslySyncedPriceList = syncedData.map((price) => price.recordId);
                    setPriceListToSync(previouslySyncedPriceList);
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleAddSync = async () => {
        try {
            if (!syncOrg.to.value || !syncOrg.from.value) {
                const orgType = !syncOrg.from.value ? "from" : "to";
                toast(`Please select organization  ${orgType} sync list`);
                return;
            }
            if (priceListToSync.length === 0) {
                toast("Please select items to sync");
                return;
            }
            setIsPending(true);
            const rObj = {
                fromOrganisationUUid: syncOrg.from.value,
                toOrganisationUUid: syncOrg.to.value,
                priceLists: priceListToSync,
            };
            const { errorCode, message } = await proceedToSyncPrices(rObj);
            if (errorCode === 0) {
                toast.success(message);
                await getSyncedPriceList(syncOrg.to.value);
            } else {
                toast.error(message);
            }
            setIsPending(false);
        } catch (error) {
            setIsPending(false);
            toast.error(error?.message);
        }
    };

    const handleSelectPrice = (state) => {
        const selectedIds = state.selectedRows.map((a) => a.recordId);
        setPriceListToSync(selectedIds);
    };

    const memoizedPriceListToSync = useMemo(() => priceListToSync, [priceListToSync.length]);

    const rowSelectCriteria = useCallback(
        (row) => {
            if ( orgSyncedPriceList.some(item => item.referencePriceList === row.recordId)) {
                return true;
            }
                return false;
        },
        [orgSyncedPriceList]
    );

    const toOrgOptionsList = useMemo(() => {
        const options = orgOptionList.filter(
            (org) => org.value !== syncOrg.from?.value
        );
        setSyncOrg((prev) => ({ ...prev, to: { ...defaultSelectValue } }));
        return options;
    }, [syncOrg.from?.value]);

    // const handleSearchSyncData = (e) => {
    //     const { value } = e.target;
    //     const _orgPriceList = orgPriceList.filter((item) => {
    //         const find = item.title.toLowerCase().indexOf(value.toLowerCase());
    //         if (find != "-1") {
    //             return item;
    //         }
    //     }).filter((item) => !priceListToSync.includes(item.recordId));
    //     const previouslySelectedRows = dataTableData.filter((row) => priceListToSync.includes(row.recordId));
    //     const newDataTableData = [...previouslySelectedRows, ..._orgPriceList];
    //     setDataTableData(newDataTableData);
    // }

    const handleSearchSyncData = (e) => {
        const { value } = e.target;
        setSearchTerm(value.toLowerCase());
    };


    useEffect(() => {
        if (searchTerm) {
          const matchedIndex = dataTableData.findIndex((row) =>
            row.title.toLowerCase().includes(searchTerm)
          );
          if (matchedIndex !== -1 && rowRefs.current[matchedIndex]) {
            rowRefs.current[matchedIndex].scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "center",
            });
          }
        }
      }, [searchTerm, dataTableData]);

    return (
        <>
            <Modal show={showSyncModal} onHide={toggleSyncModal} size="lg">
                <div className="row justify-content-between align-items-center px-3 mt-3">
                    <div className="col-6" style={{ zIndex: 6 }}>
                        <Form.Group
                            className="mb-3"
                            controlId="exampleForm.ControlInput1"
                        >
                            <Form.Label>
                                <b>From</b> Organization *
                            </Form.Label>

                            <Select
                                options={orgOptionList}
                                placeholder="Select Organization"
                                onChange={(e) => handleOrgChange(e, "from")}
                                value={syncOrg.from}
                                isDisabled
                            />
                        </Form.Group>
                    </div>
                    <div className="col-5 border-start" style={{ zIndex: 6 }}>
                        <Form.Group
                            className="mb-3"
                            controlId="exampleForm.ControlInput1"
                        >
                            <Form.Label>
                                <b>To</b> Organization *
                            </Form.Label>
                            <Select
                                options={toOrgOptionsList}
                                placeholder="Select Organization *"
                                onChange={(e) => handleOrgChange(e, "to")}
                                value={syncOrg.to}
                            />
                        </Form.Group>
                    </div>
                    <div className="col-1 d-flex align-items-center justify-content-end">
                        <Button
                            disabled={isPending}
                            variant="primary"
                            onClick={handleAddSync}
                        >
                            {isPending ? <LoaderIcon /> : "Sync"}
                        </Button>
                    </div>
                </div>
                <Modal.Body>
                    <div className='input-group searchInput mr-2' style={{ width: "48%" }}>
                        <div className='input-group-prepend'>
                            <span className='input-group-text' id='basic-addon1'>
                                <SearchRoundedIcon />
                            </span>
                        </div>
                        <input autoComplete='off' className='form-control' type='search' placeholder='Search item list' onChange={(e) => handleSearchSyncData(e)} />
                    </div>
                    {/* <input type="text" name="syncSearch" onChange={(e) => handleSearchSyncData(e)} /> */}
                    <div className="d-flex justify-content-between rh-prixelist">
                        <DataTable
                            columns={priceListColumns}
                            data={dataTableData}
                            fixedHeader
                            selectableRows
                            onSelectedRowsChange={handleSelectPrice}
                            selectableRowSelected={rowSelectCriteria}
                            ref={(el) => (el = rowRefs.current)}
                            
                        />
                        <DataTable
                            columns={priceListColumns2}
                            data={orgSyncedPriceList}
                            fixedHeader
                        // selectableRows
                        // onSelectedRowsChange={handleSelectPrice}
                        />
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default PriceSyncModal;